import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import type { SegmentedControlOption } from '../../types.ts';
import { StyledItem } from './styled.ts';

interface ItemProps<TValue> {
  option: SegmentedControlOption<TValue>;
  selectedValue: TValue;
  columns: number;
  index: number;
  totalCount: number;
  allDisabled?: boolean;
  isOptionSelected?: (optionValue: TValue, selectedValue: TValue) => boolean;
  onChange: (value: TValue) => void;
  formatOptionLabel?: (option: SegmentedControlOption<TValue>) => string;
}

export const Item = <TValue,>({
  option,
  selectedValue,
  columns,
  index,
  totalCount,
  allDisabled,
  isOptionSelected,
  onChange,
  formatOptionLabel,
}: ItemProps<TValue>) => {
  const { value, disabled: optionDisabled, label } = option;
  const checked = isOptionSelected ? isOptionSelected(value, selectedValue) : value === selectedValue;
  const disabled = allDisabled || optionDisabled;

  const handleChange = useCallbackRef(() => {
    if (!disabled) {
      onChange(value);
    }
  });

  return (
    <StyledItem
      $checked={checked}
      disabled={disabled}
      $allDisabled={allDisabled}
      $columns={columns}
      $index={index}
      $totalCount={totalCount}
      onClick={handleChange}
      tabIndex={disabled ? -1 : 0}
      data-testid={getDataTestId(label, `${value}${checked ? '-selected' : ''}`)}>
      {formatOptionLabel ? formatOptionLabel(option) : label}
    </StyledItem>
  );
};
